import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { INTERVALS, type Interval } from '../data/binance';
import { store } from '../data/store';
import { alertEngine } from '../data/alerts';
import {
  INDICATOR_DEFS,
  indicatorLabel,
  type IndicatorKind,
} from '../data/indicators';
import { ChartController } from '../chart/controller';
import { TOOLS, type DrawTool } from '../chart/drawings';
import { C } from '../styles/palette';
import { price as fmtPrice } from '../lib/num';
import { useCanvasPanel, useDismiss, useEscape, useStoreTick, useStructure } from './hooks';

/** Keyboard shortcuts for the drawing tools. */
const TOOL_KEYS: Record<string, DrawTool> = {
  v: 'cursor',
  t: 'trend',
  r: 'ray',
  h: 'hline',
  i: 'vline',
  b: 'rect',
  f: 'fib',
  m: 'measure',
};

interface ContextMenu {
  x: number;
  y: number;
  price: number;
}

/**
 * Tool icons as inline SVG.
 *
 * Unicode glyphs (╱ ▭ ↔) were the obvious shortcut and the wrong one: the
 * chosen characters resolve to different fonts on different platforms, so
 * their weight, size and vertical alignment all disagree with each other and
 * the rail looks like a ransom note. Paths render identically everywhere and
 * inherit currentColor.
 */
const ICONS: Record<string, ReactNode> = {
  cursor: (
    <>
      <path d="M3 2.5 L11.5 8.2 L7.6 8.9 L9.6 12.6 L8.1 13.4 L6.1 9.7 L3.6 12 Z" fill="currentColor" stroke="none" />
    </>
  ),
  trend: (
    <>
      <line x1="3.5" y1="12.5" x2="12.5" y2="3.5" />
      <circle cx="3.5" cy="12.5" r="1.6" />
      <circle cx="12.5" cy="3.5" r="1.6" />
    </>
  ),
  ray: (
    <>
      <line x1="3" y1="13" x2="12" y2="4" />
      <circle cx="3" cy="13" r="1.6" />
      <path d="M9.2 3.4 L13 3.4 L13 7.2" />
    </>
  ),
  hline: (
    <>
      <line x1="2" y1="8" x2="14" y2="8" />
      <circle cx="8" cy="8" r="1.7" />
    </>
  ),
  vline: (
    <>
      <line x1="8" y1="2" x2="8" y2="14" />
      <circle cx="8" cy="8" r="1.7" />
    </>
  ),
  rect: <rect x="2.5" y="4" width="11" height="8" rx="1" />,
  fib: (
    <>
      <line x1="2.5" y1="3.5" x2="13.5" y2="3.5" />
      <line x1="2.5" y1="6.6" x2="13.5" y2="6.6" opacity="0.55" />
      <line x1="2.5" y1="9.4" x2="13.5" y2="9.4" opacity="0.55" />
      <line x1="2.5" y1="12.5" x2="13.5" y2="12.5" />
    </>
  ),
  measure: (
    <>
      <line x1="8" y1="3" x2="8" y2="13" />
      <path d="M5.4 5.2 L8 2.6 L10.6 5.2" />
      <path d="M5.4 10.8 L8 13.4 L10.6 10.8" />
    </>
  ),
  erase: (
    <>
      <path d="M3 8.4 L6.6 4.8 L13 4.8 L13 11.6 L6.6 11.6 Z" />
      <line x1="8.4" y1="6.8" x2="11.2" y2="9.6" />
      <line x1="11.2" y1="6.8" x2="8.4" y2="9.6" />
    </>
  ),
  clear: (
    <>
      <path d="M3.5 4.8 L12.5 4.8 L11.7 13 L4.3 13 Z" />
      <line x1="2.4" y1="4.8" x2="13.6" y2="4.8" />
      <path d="M6.4 4.6 L6.4 3.2 L9.6 3.2 L9.6 4.6" />
    </>
  ),
};

function Icon({ name }: { name: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {ICONS[name]}
    </svg>
  );
}

export function ChartPanel() {
  useStructure();
  useStoreTick();

  const controller = useMemo(() => new ChartController(), []);
  const [, force] = useState(0);
  const [menu, setMenu] = useState<'interval' | 'indicators' | 'style' | null>(null);
  const [ctxMenu, setCtxMenu] = useState<ContextMenu | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // The controller drives its own repaints; this only refreshes the React
  // chrome (active tool highlight, indicator list) when the controller says so.
  useEffect(() => {
    controller.onChange = () => force((n) => n + 1);
    return () => {
      controller.onChange = null;
    };
  }, [controller]);

  const canvasRef = useCanvasPanel(
    (surface) => controller.draw(surface),
    { priority: 0, dirty: () => controller.dirty() },
  );

  // -- pointer wiring -------------------------------------------------------

  const localPoint = useCallback((e: PointerEvent | MouseEvent | WheelEvent) => {
    const el = canvasRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }, [canvasRef]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const onDown = (e: PointerEvent) => {
      if (e.button === 2) return;
      setCtxMenu(null);
      setMenu(null);
      el.setPointerCapture(e.pointerId);
      const p = localPoint(e);

      // Alt-click drops an alert at the clicked price — the fastest path to
      // the feature people actually use most.
      if (e.altKey) {
        const price = controller.vp.priceAt(p.y);
        if (Number.isFinite(price)) {
          alertEngine.add(store.active, roundTo(price, store.get(store.active).pricePrecision));
          controller.invalidate();
        }
        return;
      }
      controller.pointerDown(p.x, p.y, e.button, e.shiftKey);
    };

    const onMove = (e: PointerEvent) => {
      const p = localPoint(e);
      controller.pointerMove(p.x, p.y, e.shiftKey);
      el.style.cursor =
        p.x > controller.vp.plotRight
          ? 'ns-resize'
          : p.y > controller.vp.bodyBottom
            ? 'ew-resize'
            : controller.drawings.cssCursor();
    };

    const onUp = (e: PointerEvent) => {
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      controller.pointerUp();
    };

    const onLeave = () => controller.pointerLeave();

    // Non-passive so we can stop the page from scrolling while zooming.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const p = localPoint(e);
      controller.wheel(p.x, p.y, e.deltaY, e.deltaX, e.shiftKey);
    };

    const onDouble = (e: MouseEvent) => {
      const p = localPoint(e);
      controller.doubleClick(p.x);
    };

    const onContext = (e: MouseEvent) => {
      e.preventDefault();
      const p = localPoint(e);
      const rect = wrapRef.current?.getBoundingClientRect();
      setCtxMenu({
        x: e.clientX - (rect?.left ?? 0),
        y: e.clientY - (rect?.top ?? 0),
        price: controller.vp.priceAt(p.y),
      });
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('dblclick', onDouble);
    el.addEventListener('contextmenu', onContext);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('dblclick', onDouble);
      el.removeEventListener('contextmenu', onContext);
    };
  }, [controller, localPoint, canvasRef]);

  // -- keyboard -------------------------------------------------------------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.ctrlKey || e.metaKey) return;

      if (controller.keyDown(e.key)) {
        e.preventDefault();
        return;
      }
      const tool = TOOL_KEYS[e.key.toLowerCase()];
      if (tool) {
        controller.setTool(tool);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [controller]);

  useEscape(menu !== null, () => setMenu(null));
  useEscape(ctxMenu !== null, () => setCtxMenu(null));
  const menuRef = useDismiss<HTMLDivElement>(menu !== null, () => setMenu(null));
  const ctxRef = useDismiss<HTMLDivElement>(ctxMenu !== null, () => setCtxMenu(null));

  const symbol = store.active;
  const interval = store.interval;
  const st = store.get(symbol);
  const activeTool = controller.drawings.tool;

  return (
    <div className="panel">
      <div className="panel-head chart-toolbar">
        <span className="panel-title" style={{ color: C.text0 }}>
          {symbol}
        </span>

        <div className="seg">
          {(['1m', '5m', '15m', '1h', '4h', '1d'] as Interval[]).map((iv) => (
            <button
              key={iv}
              className={interval === iv ? 'on' : ''}
              onClick={() => store.setInterval(iv)}
              title={`Switch to ${iv} candles`}
            >
              {iv}
            </button>
          ))}
          <button
            className={!['1m', '5m', '15m', '1h', '4h', '1d'].includes(interval) ? 'on' : ''}
            onClick={() => setMenu(menu === 'interval' ? null : 'interval')}
            title="More intervals"
          >
            {['1m', '5m', '15m', '1h', '4h', '1d'].includes(interval) ? '···' : interval}
          </button>
        </div>

        <span className="head-sep" />

        <button
          className="btn ghost caps"
          onClick={() => setMenu(menu === 'style' ? null : 'style')}
          title="Chart style"
        >
          {controller.theme.candleStyle}
        </button>

        <button
          className={`btn ghost caps ${controller.indicators.some((i) => i.visible) ? 'accent' : ''}`}
          onClick={() => setMenu(menu === 'indicators' ? null : 'indicators')}
          title="Indicators"
        >
          Indicators · {controller.indicators.filter((i) => i.visible).length || '0'}
        </button>

        <span className="head-sep" />

        <div className="seg">
          <button
            className={controller.vp.logScale ? 'on' : ''}
            onClick={() => controller.toggleLog()}
            title="Logarithmic price scale"
          >
            LOG
          </button>
          <button
            className={controller.theme.showVolume ? 'on' : ''}
            onClick={() => controller.toggleVolume()}
            title="Volume pane"
          >
            VOL
          </button>
          <button
            className={controller.drawings.magnet ? 'on' : ''}
            onClick={() => controller.toggleMagnet()}
            title="Magnet — snap drawings to candle O/H/L/C"
          >
            MAG
          </button>
        </div>

        <span className="spacer" />

        <span className="sub" title="Last trade">
          <span className={st.ticker.direction >= 0 ? 'up' : 'down'}>
            {Number.isFinite(st.ticker.last) ? fmtPrice(st.ticker.last, st.pricePrecision) : '—'}
          </span>
        </span>
        <button className="btn sm ghost" onClick={() => controller.resetView()} title="Reset view">
          ⟲
        </button>
      </div>

      <div className="panel-body chart-wrap" ref={wrapRef}>
        <div className="draw-rail">
          {TOOLS.map((t) => (
            <button
              key={t.tool}
              className={`rail-btn ${activeTool === t.tool ? 'on' : ''}`}
              onClick={() => controller.setTool(t.tool)}
              title={`${t.label} — ${t.hint}`}
            >
              <Icon name={t.tool} />
            </button>
          ))}
          <div className="rail-sep" />
          <button
            className="rail-btn"
            onClick={() => {
              controller.drawings.deleteSelected();
              controller.invalidate();
            }}
            title="Delete selected drawing (Del)"
          >
            <Icon name="erase" />
          </button>
          <button
            className="rail-btn danger"
            onClick={() => {
              controller.drawings.clearAll();
              controller.invalidate();
            }}
            title={`Clear all drawings on ${symbol}`}
          >
            <Icon name="clear" />
          </button>
          <div className="rail-count">{controller.drawings.count || ''}</div>
        </div>

        <canvas ref={canvasRef} className="panel-canvas chart-canvas" />

        {menu === 'interval' && (
          <div className="pop" ref={menuRef} style={{ top: 4, left: 120 }}>
            <div className="pop-grid">
              {INTERVALS.map((iv) => (
                <button
                  key={iv}
                  className={`btn sm ${interval === iv ? 'active' : 'ghost'}`}
                  onClick={() => {
                    store.setInterval(iv);
                    setMenu(null);
                  }}
                >
                  {iv}
                </button>
              ))}
            </div>
          </div>
        )}

        {menu === 'style' && (
          <div className="pop" ref={menuRef} style={{ top: 4, left: 200 }}>
            {(['candles', 'bars', 'line', 'area'] as const).map((s) => (
              <div
                key={s}
                className={`pop-item ${controller.theme.candleStyle === s ? 'on' : ''}`}
                onClick={() => {
                  controller.setStyle(s);
                  setMenu(null);
                }}
              >
                {s}
              </div>
            ))}
            <div className="pop-sep" />
            <div className="pop-item" onClick={() => controller.toggleGrid()}>
              {controller.theme.showGrid ? '✓' : '　'} grid
            </div>
          </div>
        )}

        {menu === 'indicators' && (
          <div className="pop pop-wide" ref={menuRef} style={{ top: 4, left: 280 }}>
            <div className="mono-label" style={{ padding: '2px 6px 6px' }}>
              Active
            </div>
            {controller.indicators.length === 0 && (
              <div className="pop-item muted">none</div>
            )}
            {controller.indicators.map((cfg) => (
              <div key={cfg.id} className="ind-row">
                <button
                  className="ind-dot"
                  style={{ background: cfg.visible ? cfg.color : 'transparent', borderColor: cfg.color }}
                  onClick={() => controller.toggleIndicator(cfg.id)}
                  title={cfg.visible ? 'Hide' : 'Show'}
                />
                <span className={`ind-name ${cfg.visible ? '' : 'dim'}`}>
                  {indicatorLabel(cfg)}
                </span>
                {INDICATOR_DEFS[cfg.kind].params.map((p) => (
                  <input
                    key={p.key}
                    className="input ind-input"
                    type="number"
                    min={p.min}
                    max={p.max}
                    step={p.step}
                    value={cfg.params[p.key]}
                    title={p.label}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v)) controller.updateIndicator(cfg.id, { [p.key]: v });
                    }}
                  />
                ))}
                <button
                  className="btn sm ghost danger"
                  onClick={() => controller.removeIndicator(cfg.id)}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
            <div className="pop-sep" />
            <div className="mono-label" style={{ padding: '2px 6px 6px' }}>
              Add
            </div>
            {(Object.keys(INDICATOR_DEFS) as IndicatorKind[]).map((k) => (
              <div
                key={k}
                className="pop-item"
                onClick={() => controller.addIndicator(k)}
                title={INDICATOR_DEFS[k].hint}
              >
                <span style={{ width: 46, color: C.text0 }}>{INDICATOR_DEFS[k].label}</span>
                <span className="dim truncate">{INDICATOR_DEFS[k].hint}</span>
              </div>
            ))}
          </div>
        )}

        {ctxMenu && (
          <div
            className="pop"
            ref={ctxRef}
            style={{ top: ctxMenu.y, left: Math.min(ctxMenu.x, 400) }}
          >
            <div
              className="pop-item"
              onClick={() => {
                alertEngine.add(symbol, roundTo(ctxMenu.price, st.pricePrecision));
                controller.invalidate();
                setCtxMenu(null);
              }}
            >
              ⌁ Alert at {fmtPrice(ctxMenu.price, st.pricePrecision)}
            </div>
            <div className="pop-sep" />
            <div
              className="pop-item"
              onClick={() => {
                controller.resetView();
                setCtxMenu(null);
              }}
            >
              Reset view
            </div>
            <div
              className="pop-item"
              onClick={() => {
                controller.vp.resetScale();
                controller.invalidate();
                setCtxMenu(null);
              }}
            >
              Auto price scale
            </div>
            <div className="pop-sep" />
            <div
              className="pop-item"
              onClick={() => {
                controller.drawings.clearAll();
                controller.invalidate();
                setCtxMenu(null);
              }}
            >
              Clear drawings
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function roundTo(value: number, decimals: number): number {
  const f = Math.pow(10, decimals);
  return Math.round(value * f) / f;
}
